import React, { forwardRef } from "react";
import { BoxBaseProps, BoxComponent } from "./Box.types";
import { StyledBox } from "./Box.styles";
import { PolymorphicComponentPropsWithRef } from "../../utils/PolymorphicTypes";

/**
 * Box component - The core layout primitive
 *
 * A polymorphic component that can render as any HTML element
 * while maintaining type safety and style consistency.
 *
 * @example
 * // Basic usage as div (default)
 * <Box padding={2}>Content</Box>
 *
 * // As a button with proper button props
 * <Box as="button" onClick={handleClick} padding={2}>Click me</Box>
 *
 * // As a link with href and other anchor props
 * <Box as="a" href="https://example.com" color="blue">Visit site</Box>
 *
 * // With typography props
 * <Box fontSize={16} fontWeight={500} color="black">Styled text</Box>
 */
export const Box: BoxComponent = forwardRef(function Box<
  C extends React.ElementType = "div"
>(
  {
    as,
    style,
    ...otherProps
  }: PolymorphicComponentPropsWithRef<C, BoxBaseProps>,
  ref: React.Ref<Element>
) {
  // Use the element type provided in 'as' prop, or default to 'div'
  const Element = as || "div";

  return <StyledBox ref={ref} as={Element} style={style} {...otherProps} />;
}) as BoxComponent;

// Set display name for debugging
Box.displayName = "Box";

export default Box;
